import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    RiAddLine,
    RiEditLine,
    RiDeleteBinLine,
    RiArrowLeftLine,
    RiSearchLine,
    RiFilterLine
} from 'react-icons/ri';
import Button from '@/components/common/Button/Button';
import Input from '@/components/forms/Input/Input';
import Spinner from '@/components/common/Spinner/Spinner';
import { type AdminSize } from '@/hooks/admin/useAdmin';
import { useSizes, useDeleteSize } from '@/hooks/admin/useAdminSizes';
import SizeModal from '@/modals/admin/SizeModal';

const SizesAdmin: React.FC = () => {
    const [selectedSize, setSelectedSize] = useState<AdminSize | undefined>();
    const [modalOpen, setModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterActive, setFilterActive] = useState<string>('all');

    const { data: sizes = [], isLoading, error } = useSizes();
    const deleteSize = useDeleteSize();

    const filteredSizes = sizes.filter(size => {
        const matchesSearch = size.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            size.description?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterActive === 'all' ||
                            (filterActive === 'active' && size.is_active) ||
                            (filterActive === 'inactive' && !size.is_active);
        return matchesSearch && matchesFilter;
    });

    const handleCreateSize = () => {
        setSelectedSize(undefined);
        setModalOpen(true);
    };

    const handleEditSize = (size: AdminSize) => {
        setSelectedSize(size);
        setModalOpen(true);
    };

    const handleDeleteSize = async (size: AdminSize) => {
        if (window.confirm(`¿Estás seguro de que deseas eliminar el tamaño "${size.name}"?`)) {
            try {
                await deleteSize.mutateAsync(size.id);
            } catch (error) {
                console.error('Error deleting size:', error);
                alert('Error al eliminar el tamaño');
            }
        }
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedSize(undefined);
    };

    const handleModalSuccess = () => {
        // Modal will close itself on success
    };

    if (isLoading) {
        return (
            <div className="admin-page-loading">
                <Spinner size="lg" />
                <p>Cargando tamaños...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-page-error">
                <p>Error al cargar los tamaños</p>
                <Button onClick={() => window.location.reload()}>
                    Reintentar
                </Button>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <div className="admin-page-header-left">
                    <Link to="/admin" className="admin-back-link">
                        <RiArrowLeftLine />
                        <span>Volver al Dashboard</span>
                    </Link>
                    <div>
                        <h1 className="admin-page-title">Gestión de Tamaños</h1>
                        <p className="admin-page-subtitle">
                            Administra los tamaños disponibles para los productos
                        </p>
                    </div>
                </div>
                <Button
                    onClick={handleCreateSize}
                    variant="primary"
                    className="admin-create-btn"
                >
                    <RiAddLine />
                    Nuevo Tamaño
                </Button>
            </div>

            {/* Filters and Search */}
            <div className="admin-filters">
                <div className="admin-search">
                    <Input
                        name="search"
                        value={searchTerm}
                        setValue={setSearchTerm}
                        placeholder="Buscar tamaños..."
                        startContent={<RiSearchLine />}
                    />
                </div>
                <div className="admin-filter-group">
                    <label className="admin-filter-label">
                        <RiFilterLine />
                        Estado:
                    </label>
                    <select
                        value={filterActive}
                        onChange={(e) => setFilterActive(e.target.value)}
                        className="admin-filter-select"
                    >
                        <option value="all">Todos</option>
                        <option value="active">Activos</option>
                        <option value="inactive">Inactivos</option>
                    </select>
                </div>
            </div>

            {/* Sizes Table */}
            <div className="admin-table-container">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Nombre</th>
                            <th>Descripción</th>
                            <th>Costo Adicional</th>
                            <th>Orden</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredSizes.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="admin-table-empty">
                                    {searchTerm || filterActive !== 'all'
                                        ? 'No se encontraron tamaños con los filtros aplicados'
                                        : 'No hay tamaños registrados'
                                    }
                                </td>
                            </tr>
                        ) : (
                            filteredSizes.map(size => (
                                <tr key={size.id}>
                                    <td className="admin-table-name">
                                        {size.name}
                                    </td>
                                    <td className="admin-table-description">
                                        {size.description ? (
                                            <span title={size.description}>
                                                {size.description.length > 50
                                                    ? `${size.description.substring(0, 50)}...`
                                                    : size.description
                                                }
                                            </span>
                                        ) : (
                                            <span className="admin-no-description">Sin descripción</span>
                                        )}
                                    </td>
                                    <td className="admin-table-price">
                                        ${size.additional_cost.toFixed(2)}
                                    </td>
                                    <td className="admin-table-order">
                                        {size.sort_order}
                                    </td>
                                    <td>
                                        <span className={`admin-status ${size.is_active ? 'active' : 'inactive'}`}>
                                            {size.is_active ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </td>
                                    <td className="admin-table-actions">
                                        <Button
                                            onClick={() => handleEditSize(size)}
                                            variant="secondary"
                                            size="sm"
                                            className="admin-action-btn"
                                        >
                                            <RiEditLine />
                                            Editar
                                        </Button>
                                        <Button
                                            onClick={() => handleDeleteSize(size)}
                                            variant="danger"
                                            size="sm"
                                            className="admin-action-btn"
                                            disabled={deleteSize.isPending}
                                        >
                                            <RiDeleteBinLine />
                                            Eliminar
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Size Modal */}
            <SizeModal
                isOpen={modalOpen}
                onClose={handleModalClose}
                size={selectedSize}
                onSuccess={handleModalSuccess}
            />
        </div>
    );
};

export default SizesAdmin;
